import React, {useState, useEffect} from "react";
import {Col, Descriptions, Divider, Row, Tag, message, Button, Modal, Table, Badge} from "antd";
import {InfoCircleOutlined} from "@ant-design/icons";
import moment from "moment";
import {useTranslation} from "react-i18next";
import {toTurkishUpperCase} from "../../../../../utils/helpers";
import {useCourierOnboarding} from "hooks/useCourierOnboarding";
import {useStore} from "hooks/useStore";
import "./CourierSummary.css";
import {
    courierRequestCourierApprovalStatuses,
    courierTypes,
    experienceTypes,
    personalCompanySetupStatusEnums,
    vehicleOwnershipTypes
} from "constants/constants";
import {
    EXIT_REASONS,
    TERMINATION_TYPES
} from "../../courierOnboardingActionButtons/courierLifecycleConstants";
import {CourierSummaryDeviceInformation} from "./courierSummaryDeviceInformation/CourierSummaryDeviceInformation";
import {CourierSummaryZoneInformation} from "./courierSummaryZoneInformation/CourierSummaryZoneInformation";
import CourierSummaryOssNumber from "./courierSummaryOssNumber/CourierSummaryOssNumber";
import CourierSummaryMhNumber from "./courierSummaryMhNumber/CourierSummaryMhNumber";
import CourierSummaryEmploymentType from "./courierSummaryEmploymentType/CourierSummaryEmploymentType";
import CourierSummaryProjectInfo from "./courierSummaryProjectInfo/CourierSummaryProjectInfo";
import CourierSummaryManagerCompany from "./courierSummaryManagerCompany/CourierSummaryManagerCompany";
import CourierSummaryProviderCompany from "./courierSummaryProviderCompany/CourierSummaryProviderCompany";
import CourierSummaryWorkingModel from "./courierSummaryWorkingModel/CourierSummaryWorkingModel";
import {SendToAccountingButton} from "../sendToAccountingButton/SendToAccountingButton";
import {CourierEquipmentInformations} from "./courierEquipmentInformations/CourierEquipmentInformations";
import CourierSummaryOssStartDate from "./courierSummaryOssStartDate/CourierSummaryOssStartDate";
import {CourierZoneManuallyUpdateModal} from "components/_defaultComponents/CourierZoneManuallyUpdateModal/CourierZoneManuallyUpdateModal";
import {ResponsiveDataTableStyled} from "components/_defaultComponents/ResponsiveDataTable";

export const CourierSummary = ({courier, loading, reloadCourier}) => {
    const stores = useStore();
    const [selectedOssNumber, setSelectedOssNumber] = useState("");
    const [selectedOssStartDate, setSelectedOssStartDate] = useState("");
    const [selectedMhNumber, setSelectedMhNumber] = useState("");
    const [selectedEmploymentType, setSelectedEmploymentType] = useState(undefined);
    const [selectedProjectInfo, setSelectedProjectInfo] = useState(undefined);
    const [selectedManagerCompany, setSelectedManagerCompany] = useState(undefined);
    const [selectedProviderCompany, setSelectedProviderCompany] = useState(undefined);
    const [selectedWorkingModelId, setSelectedWorkingModelId] = useState(undefined);
    const [showZoneManuallyUpdateModal, setShowZoneManuallyUpdateModal] = useState(false);
    const [showLeaveHistoryModal, setShowLeaveHistoryModal] = useState(false);
    const [leaveHistory, setLeaveHistory] = useState([]);
    const [leaveHistoryLoading, setLeaveHistoryLoading] = useState(false);
    const {t, i18n} = useTranslation(["activationSteps", "activationSubSteps", "activationPhases"]);
    const {
        renderOnboardingStatus,
        renderOnboardingStep,
        renderOnboardingSubStatus
    } = useCourierOnboarding();
    const {
        fullname = "",
        workingType = undefined,
        cellPhone = "",
        email = "",
        city = "",
        county = "",
        neighborhood = undefined,
        preferredCity = undefined,
        preferredDistricts = [],
        address = "",
        iban = "",
        taxOffice = "",
        taxNumber = "",
        personCompany = "",
        personCompanyDocumentType = "personCompanyTaxCertificate",
        activationStep = "",
        activationSubStep = "",
        activationPhase = "",
        onboardingRejectReason = "",
        onboardingRejectNote = "",
        emergencyContactName = "",
        emergencyContactPhone = "",
        emergencyContactDegree = "",
        bankName = "",
        deviceInfo = {},
        isTestUser = false,
        trained = false,
        vehicleInfo = "",
        jobStartDate = undefined,
        createdByUser = undefined,
        createdById = undefined,
        createdBy = "",
        createdAt = undefined,
        applicationDate = undefined,
        updatedBy = "",
        updatedAt = undefined,
        hasP1Certificate = undefined,
        workingModel = undefined,
        workingModelId = undefined,
        experience = undefined,
        motorType = undefined,
        type: courierType = undefined,
        vehicleBrand = undefined,
        customVehicleBrand = undefined,
        vehicleModel = undefined,
        customVehicleModel = undefined,
        vehicleYear = undefined,
        licensePlate = undefined,
        vehicleOwnership = undefined,
        ossStartDate = undefined,
        mhNumber = undefined,
        hasBagRegistered = undefined,
        hasProfessionalCompetenceCertificate = undefined,
        vgid = undefined,
        preferredPartTimeHours = {},
        platform = undefined,
        drivingLicenseDate = undefined,
        zoneInfo = [],
        ossNumber = undefined,
        courierRequest = undefined,
        courierRequestInfo = undefined,
        personalCompanySetupInfo = {},
        reApplyCount = undefined,
        equipmentCompletionDate = undefined,
        firstShiftDate = undefined,
        metaData = {},
        employmentType = undefined,
        projectInfo = undefined,
        managerCompany = undefined,
        providerCompany = undefined,
        providerCompanyTransitionDate = undefined,
        lastOnboardingStatusUpdatedAt = undefined
    } = courier || {};

    const {evaluationForm: evaluationFormData, terminationForm: terminationFormData} =
        metaData || {};

    const handleShowLeaveHistory = async () => {
        setShowLeaveHistoryModal(true);
        setLeaveHistoryLoading(true);
        try {
            const result = await stores.courierStore.getLeaveHistory(courier?._id?.toString());
            if (result.success) {
                setLeaveHistory(result?.data || []);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLeaveHistoryLoading(false);
        }
    };

    const leaveHistoryColumns = [
        {
            title: "Başlangıç Tarihi",
            dataIndex: "startDate",
            key: "startDate",
            render: v => (v ? moment(v).format("DD.MM.YYYY") : "-"),
            onCell: () => ({"data-label": "Başlangıç Tarihi"})
        },
        {
            title: "Bitiş Tarihi",
            dataIndex: "endDate",
            key: "endDate",
            render: v => (v ? moment(v).format("DD.MM.YYYY") : "-"),
            onCell: () => ({"data-label": "Bitiş Tarihi"})
        },
        {
            title: "Durum",
            dataIndex: "status",
            key: "status",
            render: v => (
                <div className="leave-history-status-cell">
                    {v === "active" ? (
                        <Badge status="processing" text="Aktif" />
                    ) : (
                        <Badge status="default" text="Sonlandırıldı" />
                    )}
                </div>
            ),
            onCell: () => ({"data-label": "Durum"})
        },
        {
            title: "İzin Nedeni",
            dataIndex: "reason",
            key: "reason",
            render: v => v || "-",
            onCell: () => ({"data-label": "İzin Nedeni"})
        },
        {title: "Oluşturan", key: "createdBy", render: r => r?.createdBy?.name || "-"},
        {
            title: "Oluşturma Tarihi",
            dataIndex: "createdAt",
            key: "createdAt",
            render: v => (v ? moment(v).format("DD.MM.YYYY HH:mm") : "-"),
            onCell: () => ({"data-label": "Oluşturma Tarihi"})
        }
    ];
    const courierSeniority = jobStartDate => {
        const year = moment().diff(moment(jobStartDate), "years");
        const month = moment().diff(moment(jobStartDate), "months") - year * 12;

        if (jobStartDate === null) return t("noSeniorityData", {ns: "courierPage"});
        if (year === 0 && month === 0) {
            return 0 + " " + t("units.month.long", {ns: "common"});
        }
        if (year === 0) {
            return month + " " + t("units.month.long", {ns: "common"});
        }
        if (month === 0) {
            return year + " " + t("units.year.long", {ns: "common"});
        }
        return (
            year +
            " " +
            t("units.year.long", {ns: "common"}) +
            " " +
            month +
            " " +
            t("units.month.long", {ns: "common"})
        );
    };

    const partTimeWorkingModel = workingModelId === "664cacc30418c092e4935a1e";

    const formatPartTimeHours = preferredPartTimeHours => {
        const dayNames = [
            "Pazartesi",
            "Salı",
            "Çarşamba",
            "Perşembe",
            "Cuma",
            "Cumartesi",
            "Pazar"
        ];

        if (!preferredPartTimeHours || Object.keys(preferredPartTimeHours).length === 0) {
            return "-";
        }

        const formattedDays = [];

        Object.keys(preferredPartTimeHours)
            .sort((a, b) => a - b)
            .forEach(dayIndex => {
                const dayName = dayNames[parseInt(dayIndex)];
                const timeSlots = preferredPartTimeHours[dayIndex];

                if (timeSlots && timeSlots.length > 0) {
                    const times = timeSlots
                        .map(slot => {
                            if (typeof slot === "string") {
                                const [start, end] = slot.split("-");
                                return `${start}:00 - ${end}:00`;
                            } else if (slot.start && slot.end) {
                                return `${slot.start} - ${slot.end}`;
                            }
                            return slot;
                        })
                        .join(", ");

                    formattedDays.push(`${dayName}: ${times}`);
                }
            });

        return formattedDays.length > 0 ? formattedDays.join("\n") : "-";
    };

    const handleSaveCourier = async updates => {
        try {
            if (updates?.ossNumber !== undefined) {
                setSelectedOssNumber(updates.ossNumber);
            }

            if (updates?.ossStartDate !== undefined) {
                setSelectedOssStartDate(updates.ossStartDate);
            }

            if (updates?.mhNumber !== undefined) {
                setSelectedMhNumber(updates.mhNumber);
            }

            if (updates?.employmentType !== undefined) {
                setSelectedEmploymentType(updates.employmentType);
            }

            if (updates?.projectInfo !== undefined) {
                setSelectedProjectInfo(updates.projectInfo);
            }

            if (updates?.managerCompany !== undefined) {
                setSelectedManagerCompany(updates.managerCompany);
            }

            if (updates?.providerCompany !== undefined) {
                setSelectedProviderCompany(updates.providerCompany);
            }

            if (updates?.workingModelId !== undefined) {
                setSelectedWorkingModelId(updates.workingModelId);
            }

            const success = await stores.courierStore.updateCourier({
                courier: {
                    ...courier,
                    ...updates
                }
            });
            if (success) {
                reloadCourier();
                return;
            } else {
                throw new Error("ÖSS Poliçe Numarası güncellenemedi");
            }
        } catch (error) {
            message.error(error.message || "ÖSS Poliçe Numarası güncellenemedi");
        }
    };

    useEffect(() => {
        setSelectedOssNumber(courier?.ossNumber);
        setSelectedOssStartDate(courier?.ossStartDate);
        setSelectedMhNumber(courier?.mhNumber);
        setSelectedEmploymentType(courier?.employmentType);
        setSelectedProjectInfo(courier?.projectInfo);
        setSelectedManagerCompany(courier?.managerCompany);
        setSelectedProviderCompany(courier?.providerCompany);
        setSelectedWorkingModelId(courier?.workingModelId);
    }, [
        courier?.ossNumber,
        courier?.ossStartDate,
        courier?.mhNumber,
        courier?.employmentType,
        courier?.projectInfo,
        courier?.managerCompany,
        courier?.providerCompany,
        courier?.workingModelId
    ]);

    return loading ? (
        <>Yükleniyor...</>
    ) : (
        <>
            <Row gutter={[16, 16]} style={{marginBottom: "16px"}}>
                <Col span={24} md={24} lg={12}>
                    <Row style={{flexDirection: "column", marginBottom: "16px"}}>
                        <h3>Kurye Durumu</h3>
                        <Descriptions
                            bordered
                            size={"small"}
                            column={1}
                            className="courierSummaryDescriptions"
                        >
                            <Descriptions.Item label="Kurye Durumu">
                                <span style={{fontSize: "16px", fontWeight: "bold"}}>
                                    {renderOnboardingStatus({
                                        activationStep
                                    })}
                                </span>
                            </Descriptions.Item>
                            <Descriptions.Item label="İşe Alım Durumu">
                                <span style={{fontSize: "16px", fontWeight: "bold"}}>
                                    {renderOnboardingSubStatus({
                                        activationSubStep,
                                        activationStep: activationStep
                                    })}
                                </span>
                                {activationSubStep === "documentsInReview" &&
                                activationStep !== "rejected" ? (
                                    <>
                                        <br />
                                        <Tag color="red" style={{whiteSpace: "normal"}}>
                                            Kurye bu adımda evrak düzenleme yapamaz
                                        </Tag>
                                    </>
                                ) : (
                                    <></>
                                )}
                            </Descriptions.Item>
                            <Descriptions.Item label="Süreçte Bulunduğu Faz">
                                <span style={{fontSize: "16px", fontWeight: "bold"}}>
                                    {renderOnboardingStep({activationPhase})}
                                </span>
                            </Descriptions.Item>

                            <Descriptions.Item label="Red Sebebi">
                                {onboardingRejectReason || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Kurye Süreçten Çekilme Notu">
                                {onboardingRejectNote || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Son İzin Tarihi">
                                <Row align="middle" gutter={8}>
                                    <Col>
                                        {metaData?.lastLeave?.status === "active" ? (
                                            <Badge status="processing" text="Şu an izinli" />
                                        ) : null}
                                        {metaData?.lastLeave?.startDate ? (
                                            <span
                                                style={{
                                                    marginLeft:
                                                        metaData?.lastLeave?.status === "active"
                                                            ? 8
                                                            : 0
                                                }}
                                            >
                                                {moment(metaData?.lastLeave?.startDate).format(
                                                    "DD.MM.YYYY"
                                                )}
                                                {" – "}
                                                {metaData?.lastLeave?.endDate
                                                    ? moment(metaData?.lastLeave?.endDate).format(
                                                          "DD.MM.YYYY"
                                                      )
                                                    : "Devam ediyor"}
                                            </span>
                                        ) : (
                                            metaData?.lastLeave?.status !== "active" && (
                                                <span>-</span>
                                            )
                                        )}
                                    </Col>
                                    <Col>
                                        <Button
                                            type="link"
                                            icon={<InfoCircleOutlined />}
                                            onClick={handleShowLeaveHistory}
                                            style={{padding: 0}}
                                        >
                                            İzin Geçmişini Görüntüle
                                        </Button>
                                    </Col>
                                </Row>
                            </Descriptions.Item>
                        </Descriptions>
                    </Row>
                    <Row style={{flexDirection: "column", marginBottom: "16px"}}>
                        <h3>İletişim Bilgileri</h3>
                        <Descriptions
                            bordered
                            size={"small"}
                            column={1}
                            className="courierSummaryDescriptions"
                        >
                            <Descriptions.Item label="Telefon">{cellPhone}</Descriptions.Item>
                            <Descriptions.Item label="E-mail">{email}</Descriptions.Item>
                            <Descriptions.Item
                                label={t("fields.emergencyContactName", {ns: "common"})}
                            >
                                {emergencyContactName}
                            </Descriptions.Item>
                            <Descriptions.Item
                                label={t("fields.emergencyContactPhone", {ns: "common"})}
                            >
                                {emergencyContactPhone}
                            </Descriptions.Item>
                            <Descriptions.Item
                                label={t("fields.emergencyContactDegree", {ns: "common"})}
                            >
                                {emergencyContactDegree}
                            </Descriptions.Item>
                        </Descriptions>
                    </Row>
                    <Row style={{flexDirection: "column", marginBottom: "16px"}}>
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: "8px"
                            }}
                        >
                            <h3>Şahıs Şirketi Bilgileri</h3>
                            <SendToAccountingButton
                                courier={courier}
                                reloadCourier={reloadCourier}
                            />
                        </div>
                        <Descriptions
                            bordered
                            size={"small"}
                            column={1}
                            style={{border: "1px solid #e8e8e8"}}
                        >
                            <Descriptions.Item label="Şahıs Şirketi Adı">
                                {personCompany}
                            </Descriptions.Item>

                            <Descriptions.Item label="Vergi Dairesi">{taxOffice}</Descriptions.Item>
                            <Descriptions.Item label="Vergi Numarası">
                                {taxNumber}
                            </Descriptions.Item>
                            <Descriptions.Item label="Şahıs Şirketi Belge Seçimi">
                                {personCompanyDocumentType === "businessStartNoticeDoc"
                                    ? "İşe Başlama Bildirim Belgesi"
                                    : "Şahıs Şirketi Vergi Levhası"}
                            </Descriptions.Item>
                            <Descriptions.Item label={t("fields.bankName", {ns: "common"})}>
                                {bankName}
                            </Descriptions.Item>
                            <Descriptions.Item label="IBAN">{iban}</Descriptions.Item>
                            {personalCompanySetupInfo?.status && (
                                <Descriptions.Item label="Kurulum Durumu">
                                    <Tag
                                        color={
                                            personalCompanySetupInfo.status ===
                                            personalCompanySetupStatusEnums.COMPLETED
                                                ? "green"
                                                : personalCompanySetupInfo.status ===
                                                  personalCompanySetupStatusEnums.SUPPORT_REQUESTED
                                                ? "orange"
                                                : personalCompanySetupInfo.status ===
                                                  personalCompanySetupStatusEnums.SELF_SETUP_SELECTED
                                                ? "blue"
                                                : "default"
                                        }
                                    >
                                        {personalCompanySetupInfo.status ===
                                        personalCompanySetupStatusEnums.COMPLETED
                                            ? "Tamamlandı"
                                            : personalCompanySetupInfo.status ===
                                              personalCompanySetupStatusEnums.SUPPORT_REQUESTED
                                            ? "Destek Bekleniyor"
                                            : personalCompanySetupInfo.status ===
                                              personalCompanySetupStatusEnums.SELF_SETUP_SELECTED
                                            ? "Kendi Kuracak"
                                            : "Cevaplanmamış"}
                                    </Tag>
                                </Descriptions.Item>
                            )}
                            {personalCompanySetupInfo?.sentToAccounting && (
                                <>
                                    <Descriptions.Item label="Muhasebeye Gönderildi">
                                        <Tag color="green">Evet</Tag>
                                    </Descriptions.Item>
                                    {personalCompanySetupInfo?.sentAt && (
                                        <Descriptions.Item label="Gönderim Tarihi">
                                            {moment(personalCompanySetupInfo.sentAt).format(
                                                "DD.MM.YYYY HH:mm"
                                            )}
                                        </Descriptions.Item>
                                    )}
                                </>
                            )}
                        </Descriptions>
                    </Row>
                </Col>
                <Col span={24} md={24} lg={12}>
                    <Row style={{flexDirection: "column", marginBottom: "16px"}}>
                        <h3>Adres Bilgileri</h3>
                        <Descriptions
                            bordered
                            size={"small"}
                            column={1}
                            className="courierSummaryDescriptions"
                        >
                            <Descriptions.Item label="Şehir">
                                {toTurkishUpperCase(city)}
                            </Descriptions.Item>
                            <Descriptions.Item label="İlçe">
                                {toTurkishUpperCase(county)}
                            </Descriptions.Item>
                            <Descriptions.Item label="Mahalle">
                                {neighborhood?.name || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Adres">{address || "-"}</Descriptions.Item>
                        </Descriptions>
                    </Row>
                    <Row style={{flexDirection: "column", marginBottom: "16px"}}>
                        <h3>Çalışma Bilgileri</h3>
                        <Descriptions
                            bordered
                            size={"small"}
                            column={1}
                            className="courierSummaryDescriptions"
                        >
                            <Descriptions.Item label="Kurye Modeli">
                                {courierTypes.find(ct => ct.value === courierType)?.name || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Çalışma Zamanı">
                                <CourierSummaryWorkingModel
                                    workingModel={workingModel}
                                    workingModelId={workingModelId}
                                    selectedWorkingModelId={selectedWorkingModelId}
                                    onSave={handleSaveCourier}
                                />
                            </Descriptions.Item>
                            <Descriptions.Item label="Çalışma Şekli">
                                <CourierSummaryEmploymentType
                                    employmentType={employmentType}
                                    selectedEmploymentType={selectedEmploymentType}
                                    onSave={handleSaveCourier}
                                />
                            </Descriptions.Item>
                            <Descriptions.Item label="Sağlayıcı Firma">
                                <CourierSummaryProviderCompany
                                    providerCompany={providerCompany}
                                    selectedProviderCompany={selectedProviderCompany}
                                    vionTransitionDate={providerCompanyTransitionDate}
                                    courierId={courier?._id?.toString()}
                                    onProviderCompanyUpdated={reloadCourier}
                                />
                            </Descriptions.Item>
                            <Descriptions.Item label="Sağlayıcı Firma Geçiş Tarihi">
                                {providerCompanyTransitionDate
                                    ? moment(providerCompanyTransitionDate).format("DD.MM.YYYY")
                                    : "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Proje Bilgisi">
                                <CourierSummaryProjectInfo
                                    projectInfo={projectInfo}
                                    selectedProjectInfo={selectedProjectInfo}
                                    onSave={handleSaveCourier}
                                />
                            </Descriptions.Item>
                            <Descriptions.Item label="Yönetici Firma">
                                <CourierSummaryManagerCompany
                                    managerCompany={managerCompany}
                                    selectedManagerCompany={selectedManagerCompany}
                                    onSave={handleSaveCourier}
                                />
                            </Descriptions.Item>
                            {partTimeWorkingModel && (
                                <Descriptions.Item label="Çalışma Saati Tercihleri">
                                    <div style={{whiteSpace: "pre-wrap"}}>
                                        {formatPartTimeHours(preferredPartTimeHours)}
                                    </div>
                                </Descriptions.Item>
                            )}
                            <Descriptions.Item label="Çalışmak İstenen Şehir">
                                {preferredCity?.name || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Çalışmak İstenen İlçeler">
                                {preferredDistricts?.map(district => district?.name)?.join(", ") ||
                                    "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Kuryelik Deneyimi">
                                {experienceTypes[experience]?.tr || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Motor Tipi">
                                {motorType?.label?.tr || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Araç Markası">
                                {vehicleBrand?.name || customVehicleBrand || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Araç Modeli">
                                {vehicleModel?.name || customVehicleModel || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Araç Modeli Yılı">
                                {vehicleYear || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Plaka">
                                {licensePlate || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Araç Sahibi">
                                {vehicleOwnershipTypes.find(type => type.value === vehicleOwnership)
                                    ?.name || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="P1* Belgesi Durumu">
                                {typeof hasP1Certificate === "boolean"
                                    ? hasP1Certificate
                                        ? "Var"
                                        : "Yok"
                                    : "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Sürücü Belgesi Veriliş Tarihi">
                                {drivingLicenseDate
                                    ? moment(drivingLicenseDate).format("DD.MM.YYYY")
                                    : "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="ÖSS Poliçe Numarası">
                                <CourierSummaryOssNumber
                                    ossNumber={ossNumber}
                                    onSave={handleSaveCourier}
                                    selectedOssNumber={selectedOssNumber}
                                />
                            </Descriptions.Item>
                            <Descriptions.Item label="ÖSS Başlangıç Tarihi">
                                <CourierSummaryOssStartDate
                                    ossStartDate={ossStartDate}
                                    onSave={handleSaveCourier}
                                    selectedOssStartDate={selectedOssStartDate}
                                />
                            </Descriptions.Item>
                            <Descriptions.Item label="MH Numarası">
                                <CourierSummaryMhNumber
                                    mhNumber={mhNumber}
                                    onSave={handleSaveCourier}
                                    selectedMhNumber={selectedMhNumber}
                                />
                            </Descriptions.Item>
                            <Descriptions.Item label="Çanta Ruhsata Kayıtlı mı?">
                                {typeof hasBagRegistered === "boolean"
                                    ? hasBagRegistered
                                        ? "Evet"
                                        : "Hayır"
                                    : "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Mesleki Yeterlilik Belgesi">
                                {typeof hasProfessionalCompetenceCertificate === "boolean"
                                    ? hasProfessionalCompetenceCertificate
                                        ? "Evet"
                                        : "Hayır"
                                    : "-"}
                            </Descriptions.Item>
                        </Descriptions>
                    </Row>
                </Col>
                <Divider />
                <Col span={24} md={24} lg={12}>
                    <Row style={{flexDirection: "column", marginBottom: "16px"}}>
                        <h3>Diğer Bilgiler</h3>
                        <Descriptions
                            bordered
                            size={"small"}
                            column={1}
                            className="courierSummaryDescriptions"
                        >
                            <Descriptions.Item label={"Kurye Talebi"}>
                                {courierRequest?.name || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label={"Kurye Talebi Durumu"}>
                                {courierRequestCourierApprovalStatuses.find(
                                    status =>
                                        status.value === courierRequestInfo?.requestApproveStatus
                                )?.name || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label={t("fields.testUser", {ns: "common"})}>
                                {isTestUser
                                    ? t("other.yes", {ns: "common"})
                                    : t("other.no", {ns: "common"})}
                            </Descriptions.Item>
                            <Descriptions.Item
                                label={`${t("courierPageFields.seniority", {
                                    ns: "courierPage"
                                })}(${t("units.month.long", {ns: "common"})})`}
                            >
                                {courierSeniority(jobStartDate)}
                            </Descriptions.Item>
                            <Descriptions.Item label={t("fields.createdBy", {ns: "common"})}>
                                {(createdById ? createdByUser?.fullName : createdBy) ||
                                    `${fullname} (Başvuru Yapan)` ||
                                    "-"}
                            </Descriptions.Item>
                            <Descriptions.Item
                                label={t("fields.courierCreatedAt", {ns: "courierDetail"})}
                            >
                                {createdAt ? moment(createdAt).format("DD-MM-YYYY hh:mm:ss") : "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label={"Başvuru Tarihi"}>
                                {applicationDate
                                    ? moment(applicationDate).format("DD-MM-YYYY hh:mm:ss")
                                    : "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label={"Başvuru Sayısı"}>
                                {reApplyCount ? reApplyCount : "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label={"Son Durum Güncelleme Tarihi"}>
                                {lastOnboardingStatusUpdatedAt
                                    ? moment(lastOnboardingStatusUpdatedAt).format(
                                          "DD-MM-YYYY hh:mm:ss"
                                      )
                                    : "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label={"Ekipman Tamamlama Tarihi"}>
                                {equipmentCompletionDate
                                    ? moment(equipmentCompletionDate).format("DD-MM-YYYY hh:mm:ss")
                                    : "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label={"İlk Vardiya Tarihi"}>
                                {firstShiftDate ? moment(firstShiftDate).format("DD-MM-YYYY") : "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label={t("fields.updatedBy", {ns: "common"})}>
                                {updatedBy || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item
                                label={t("fields.courierUpdatedAt", {ns: "courierDetail"})}
                            >
                                {updatedAt ? moment(updatedAt).format("DD-MM-YYYY hh:mm:ss") : "-"}
                            </Descriptions.Item>
                            <Descriptions.Item
                                label={t("fields.appliedPlatform", {ns: "courierDetail"})}
                            >
                                {platform ? t(`fields.${platform}`, {ns: "courierDetail"}) : "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Arkadaşını Getir Ref. Kod">
                                <span data-testid="courierVGID">{vgid || "-"}</span>
                            </Descriptions.Item>
                        </Descriptions>
                    </Row>
                </Col>
                <Col span={24} md={24} lg={12}>
                    <Row style={{flexDirection: "column", marginBottom: "16px"}}>
                        <h3>Aktif Mobil Cihaz Bilgileri</h3>
                        <CourierSummaryDeviceInformation
                            courierId={courier._id}
                            deviceInfo={deviceInfo}
                        />
                    </Row>
                </Col>
                <Col span={24} md={24} lg={12}>
                    <Row style={{flexDirection: "column", marginBottom: "16px"}}>
                        <h3>Ekipman Bilgileri</h3>
                        <CourierEquipmentInformations
                            courierEquipments={courier?.courierEquipments}
                        />
                    </Row>
                </Col>
                <Col span={24} md={24} lg={12}>
                    <Row style={{flexDirection: "column", marginBottom: "16px"}}>
                        <Row
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center"
                            }}
                        >
                            <h3>Bölge Bilgileri</h3>
                            {zoneInfo?.length > 1 &&
                            stores.meStore.checkRole("updateCourierZoneManually") &&
                            stores.meStore.checkPermission("couriers.edit") ? (
                                <Button
                                    type="primary"
                                    size="small"
                                    onClick={() => setShowZoneManuallyUpdateModal(true)}
                                >
                                    Bölge Seçimi Yap
                                </Button>
                            ) : (
                                <></>
                            )}
                        </Row>
                        <CourierSummaryZoneInformation zoneInfo={zoneInfo} />
                    </Row>
                </Col>
            </Row>
            {/* Değerlendirme Form Bilgisi */}
            {evaluationFormData && (
                <Row>
                    <Col span={24}>
                        <Divider />
                        <h3>Değerlendirme Form Bilgisi</h3>
                        <Descriptions bordered column={2} size="small">
                            <Descriptions.Item label="Fesih Durumu">
                                {TERMINATION_TYPES.find(
                                    t => t.value === evaluationFormData.terminationType
                                )?.label || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Çıkış Nedeni">
                                {EXIT_REASONS.find(r => r.value === evaluationFormData.reason)
                                    ?.label || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="İhbar Bitiş Tarihi">
                                {evaluationFormData.noticeEndDate
                                    ? moment(evaluationFormData.noticeEndDate).format("DD.MM.YYYY")
                                    : "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Bildirilen Çıkış Tarihi">
                                {evaluationFormData.reportedQuitDate
                                    ? moment(evaluationFormData.reportedQuitDate).format(
                                          "DD.MM.YYYY"
                                      )
                                    : "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="İhbar Kesintisi">
                                {evaluationFormData.noticeDeduction ? "Evet" : "Hayır"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Çıkış Açıklaması (Operasyon)">
                                {evaluationFormData.operationNote || "-"}
                            </Descriptions.Item>
                        </Descriptions>
                    </Col>
                </Row>
            )}

            {/* İşten Çıkarma Form Bilgisi */}
            {terminationFormData && (
                <Row>
                    <Col span={24}>
                        <Divider />
                        <h3>İşten Çıkarma Form Bilgisi</h3>
                        <Descriptions bordered column={2} size="small">
                            <Descriptions.Item label="Resmi Çıkış Tarihi">
                                {terminationFormData.officialQuitDate
                                    ? moment(terminationFormData.officialQuitDate).format(
                                          "DD.MM.YYYY"
                                      )
                                    : "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="ÖSS Bitiş Tarihi">
                                {terminationFormData.ossEndDate
                                    ? moment(terminationFormData.ossEndDate).format("DD.MM.YYYY")
                                    : "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Çıkış Nedeni">
                                {EXIT_REASONS.find(r => r.value === terminationFormData.reason)
                                    ?.label || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="PTT Çıkışlı">
                                {terminationFormData.pttTerminated ? "Evet" : "Hayır"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Çıkış Açıklaması (İK)" span={2}>
                                {terminationFormData.hrNote || "-"}
                            </Descriptions.Item>
                        </Descriptions>
                    </Col>
                </Row>
            )}

            <CourierZoneManuallyUpdateModal
                courierId={courier?._id?.toString()}
                visibility={showZoneManuallyUpdateModal}
                onClose={() => setShowZoneManuallyUpdateModal(false)}
                onComplete={() => {
                    reloadCourier();
                }}
            />

            {/* İzin Geçmişi Modal */}
            <Modal
                title="İzin Geçmişi"
                visible={showLeaveHistoryModal}
                onCancel={() => setShowLeaveHistoryModal(false)}
                footer={null}
                width={800}
            >
                <ResponsiveDataTableStyled
                    className="leave-history-table"
                    columns={leaveHistoryColumns}
                    dataSource={leaveHistory}
                    loading={leaveHistoryLoading}
                    rowKey="_id"
                    pagination={{pageSize: 10}}
                    size="small"
                />
            </Modal>
        </>
    );
};
